import {useGetGroupFiltersQuery} from '../../../../api/services/groupService';
import {useGetStudentFiltersQuery, useLazyGetStudentFiltersQuery} from '../../../../api/services/studentService';
import {
    useGetFormEmployeeAssignmentsQuery,
    useGetFormStudentsAssignmentsQuery
} from '../../../../api/services/formAssignmentsService';
import FORM_AUDIENCE_TYPE from '../../../../constants/FormAudienceType';
import React, {useEffect, useMemo, useState} from 'react';
import SettingsSectionBase, {SettingsSection} from './SettingsSection';
import {Box, Checkbox, FormControlLabel, Tooltip, Typography} from '@mui/material';
import EmailsInput from '../../../../shared-components/EmailsInput';
import AdmicityTableV2 from '../../../../shared-components/Table/V2/AdmicityTableV2';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import PeopleOutlineOutlinedIcon from '@mui/icons-material/PeopleOutlineOutlined';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import AddStudentGroupDialog from './AddStudentGroupDialog';
import {mergeArraysByKey, mergeArraysByKeys} from '../../../../utility/mergeArraysByKey';
import {validateAccessLevel, validateGroupFilters} from '../../../../utility/validationUtil';
import {GROUP_MEMBERS_TYPE} from '../../../../constants/GroupMembersType';
import PaginatedAutocomplete from '../../../../shared-components/PaginatedAutocomplete';
import {useGetCurrentSchoolQuery} from '../../../../api/services/schoolManagementService';
import {useLazyGetAllEmployeeProfilesQuery} from '../../../../api/services/employeeService';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import AssignmentIndOutlinedIcon from '@mui/icons-material/AssignmentIndOutlined';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import AdmicityStatus from '../../../../shared-components/AdmicityStatus';
import Divider from '@mui/material/Divider';
import WarningIcon from '@mui/icons-material/Warning';
import FORM_ASSIGNMENT_TYPE from '../../../../constants/FormAssignmentType';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import EmailIcon from '@mui/icons-material/Email';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

/* eslint-disable react/prop-types */

const options = {
    [FORM_ASSIGNMENT_TYPE.students]: {
        id: FORM_ASSIGNMENT_TYPE.students,
        label: 'Student Recipients',
        icon: <PeopleOutlineOutlinedIcon fontSize="small"/>,
    },
    [FORM_ASSIGNMENT_TYPE.employees]: {
        id: FORM_ASSIGNMENT_TYPE.employees,
        label: 'Employee Recipients',
        icon: <AssignmentIndOutlinedIcon fontSize="small"/>,
    },
};

const getGroupFilterStatusInfo = (isExcluded) =>
    ({
        label: isExcluded ? 'Excluded' : 'Included',
        color: isExcluded ? 'red' : 'green'
    });

const RegularFormSettings = (
    {
        audience,
        formId,
        settings,
        strictRecipientsFiltering,
        isFormLive,
        isFormArchived,
        externalUsers,
        accessLevel,
        onSettingsChange,
        onSetStrictRecipientsFiltering
    }) => {
    const {
        groupFilters,
        studentFilters,
        applicationFilters,
        enquiryFilters,
        employeeFilters,
        externalUserEmailAssignments,
        assignmentType
    } = settings;

    const [originalSettings, setOriginalSettings] = useState({
        groupFilters: [],
        studentFilters: [],
        applicationFilters: [],
        enquiryFilters: [],
        employeeFilters: [],
        externalUserEmailAssignments: [],
        assignmentType: FORM_ASSIGNMENT_TYPE.none
    });

    const fetchEmployeeAssignments =
        audience === FORM_AUDIENCE_TYPE.employees ||
        audience === FORM_AUDIENCE_TYPE.externalUsers;

    const fetchStudentsAssignments =
        audience === FORM_AUDIENCE_TYPE.students ||
        audience === FORM_AUDIENCE_TYPE.parentsForChildCompletion ||
        audience === FORM_AUDIENCE_TYPE.previousSchool ||
        audience === FORM_AUDIENCE_TYPE.externalUsers;

    const {data: schoolInfo} = useGetCurrentSchoolQuery({}, {skip: !audience || audience === FORM_AUDIENCE_TYPE.employees});
    const {data} = useGetGroupFiltersQuery({}, {skip: !audience});

    const {
        data: studentFormAssignmentData,
        isSuccess: isFetchStudentFormAssignmentSuccess
    } = useGetFormStudentsAssignmentsQuery({formId}, {
        skip: !fetchStudentsAssignments && !(
            originalSettings.studentFilters.length ||
            originalSettings.applicationFilters.length ||
            originalSettings.enquiryFilters.length ||
            originalSettings.groupFilters)
    });

    const {
        data: employeeFormAssignmentData,
        isSuccess: isFetchEmployeeFormAssignmentSuccess
    } = useGetFormEmployeeAssignmentsQuery({formId}, {
        skip: !fetchEmployeeAssignments && !(originalSettings.employeeFilters.length || originalSettings.groupFilters)
    });

    const assignments = studentFormAssignmentData ?? employeeFormAssignmentData ?? {
        personAssignments: [],
        groupFilters: [],
    };

    const {data: studentsData} = useGetStudentFiltersQuery({
        body: {
            studentIds: studentFormAssignmentData?.personAssignments?.map(x => x.personId)
        }
    }, {skip: !audience || !studentFormAssignmentData || studentFormAssignmentData.personAssignments.length === 0});

    useEffect(() => {
        if (assignments.groupFilters) {
            const groupFilters = assignments.groupFilters.map(x => ({
                id: x.filterId,
                displayName: x.displayName,
                groupId: x.id,
                excluded: x.excluded,
                deleted: x.deleted
            }));

            onSettingsChange(prev => ({
                ...prev,
                groupFilters,
                validation: {
                    ...validateGroupFilters(groupFilters),
                    ...validateAccessLevel(accessLevel, groupFilters)
                }
            }));
            setOriginalSettings(prev => ({...prev, groupFilters}));
        }
    }, [assignments.groupFilters]);

    useEffect(() => {
        if (!isFetchStudentFormAssignmentSuccess) return;
        if (!studentFormAssignmentData || !studentsData?.length) return;

        const studentsMap = studentsData.reduce((acc, item) => {
            acc[item.id] = item;
            return acc;
        }, {});

        const filters = studentFormAssignmentData.personAssignments.reduce((acc, item) => {
            const pupil = studentsMap[item.personId];

            if (!pupil) {
                return acc;
            }

            const filterItem = {
                id: item.personId,
                label: item.displayName,
                assignmentId: item.id
            };

            if (pupil.isApplicant) {
                acc.applicationFilters.push(filterItem);
            } else if (pupil.isStudent) {
                acc.studentFilters.push(filterItem);
            } else if (pupil.isEnquiry) {
                acc.enquiryFilters.push(filterItem);
            }

            return acc;
        }, {studentFilters: [], applicationFilters: [], enquiryFilters: []});

        onSettingsChange(prev => ({...prev, ...filters, assignmentType: FORM_ASSIGNMENT_TYPE.students}));
        setOriginalSettings(prev => ({...prev, ...filters, assignmentType: FORM_ASSIGNMENT_TYPE.students}));
    }, [isFetchStudentFormAssignmentSuccess, studentFormAssignmentData, studentsData]);

    useEffect(() => {
        if (!isFetchEmployeeFormAssignmentSuccess) return;
        if (!employeeFormAssignmentData?.personAssignments?.length) return;

        const employeeFilters = employeeFormAssignmentData.personAssignments.map(item => ({
            id: item.personId,
            assignmentId: item.id,
            label: item.displayName
        }));

        onSettingsChange(prev => ({...prev, employeeFilters, assignmentType: FORM_ASSIGNMENT_TYPE.employees}));
        setOriginalSettings(prev => ({...prev, employeeFilters, assignmentType: FORM_ASSIGNMENT_TYPE.employees}));
    }, [isFetchEmployeeFormAssignmentSuccess, employeeFormAssignmentData]);

    useEffect(() => {
        if (externalUsers) {
            onSettingsChange(prev => ({
                ...prev,
                externalUserEmailAssignments: externalUsers
            }));
            setOriginalSettings(prev => ({
                ...prev,
                externalUserEmailAssignments: externalUsers
            }));
        }
    }, [externalUsers]);

    const handleChangeGroupFilters = filters =>
        onSettingsChange(prev => ({
            ...prev,
            groupFilters: filters,
            validation: {
                ...validateGroupFilters(filters),
                ...validateAccessLevel(accessLevel, filters)
            }
        }));

    const handleStudentFiltersChange = value =>
        onSettingsChange(prev => ({
            ...prev,
            ...value
        }));

    const handleEmployeeFiltersChange = filters =>
        onSettingsChange(prev => ({
            ...prev,
            employeeFilters: filters
        }));

    const handleAssignmentTypeChange = (e) => {
        const newType = e.target.value;

        onSettingsChange((prev) => {
            const nextFilters = {
                studentFilters: mergeArraysByKey([], originalSettings.studentFilters),
                applicationFilters: mergeArraysByKey([], originalSettings.applicationFilters),
                enquiryFilters: mergeArraysByKey([], originalSettings.enquiryFilters),
                employeeFilters: mergeArraysByKey([], originalSettings.employeeFilters),
                groupFilters: mergeArraysByKeys([], originalSettings.groupFilters, ['id', 'excluded']),
            };

            return {...prev, assignmentType: newType, ...nextFilters};
        });
    };

    return (
        <Box display="flex" flexDirection="column" gap={2}>
            {
                audience === FORM_AUDIENCE_TYPE.externalUsers && (
                    <>
                        <SettingsSectionBase
                            header="External User Recipients"
                            headerIcon={<PublicOutlinedIcon fontSize="small"/>}
                            subHeader="Add email addresses for recipients without an account or linked profile."
                        >
                            <SettingsSection>
                                <EmailsInput
                                    disabled={isFormArchived}
                                    values={externalUserEmailAssignments.filter(x => !x.isDeleted)}
                                    onChange={emails => {
                                        if (isFormArchived) return;
                                        onSettingsChange(prev => ({
                                        ...prev,
                                        externalUserEmailAssignments: mergeArraysByKey(
                                            emails,
                                            originalSettings.externalUserEmailAssignments,
                                            'email')
                                        }));
                                    }}
                                    onBlur={email => {
                                        if (isFormArchived) return;
                                        onSettingsChange(prev => ({
                                        ...prev,
                                        externalUserEmailAssignments: mergeArraysByKey(
                                            [...settings.externalUserEmailAssignments, {email}],
                                            originalSettings.externalUserEmailAssignments,
                                            'email')
                                        }));
                                    }}
                                />
                            </SettingsSection>
                        </SettingsSectionBase>
                        <Divider sx={{my: 3}}/>
                        <SettingsSectionBase
                            header="Select Recipient Type"
                            headerIcon={<EmailIcon fontSize="small"/>}
                            subHeader="Choose the recipient group type."
                        >
                            <SettingsSection>
                                <FormControl
                                    variant="outlined"
                                    disabled={
                                        isFormArchived ||
                                        (settings && ([
                                        ...settings.groupFilters,
                                        ...settings?.employeeFilters,
                                        ...settings?.studentFilters,
                                        ...settings?.applicationFilters,
                                        ...settings?.enquiryFilters,
                                        ].filter(x => x.isDeleted == null || !x.isDeleted).length > 0))
                                    }
                                >
                                    <InputLabel id="assignment-type-label">Assignment Type</InputLabel>
                                    <Select
                                        labelId="assignment-type-label"
                                        value={assignmentType}
                                        onChange={handleAssignmentTypeChange}
                                        label="Assignment Type"
                                        renderValue={(selected) => {
                                            const {icon, label} = options[selected];
                                            return (
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    {icon}
                                                    {label}
                                                </Box>
                                            );
                                        }}
                                     variant={'outlined'}
                                    >
                                        {Object.entries(options).map(([key, {id, label, icon}]) =>
                                            (
                                                <MenuItem key={key} value={id}>
                                                    <Box display="flex" alignItems="center" gap={1}>
                                                        {icon}
                                                        {label}
                                                    </Box>
                                                </MenuItem>
                                            ))}
                                    </Select>
                                </FormControl>
                                <Box sx={{my: 3}}/>
                            </SettingsSection>
                        </SettingsSectionBase>
                    </>
                )}

            {assignmentType === FORM_ASSIGNMENT_TYPE.employees &&
                (audience === FORM_AUDIENCE_TYPE.employees || audience === FORM_AUDIENCE_TYPE.externalUsers) && (
                    <EmployeeFiltersSelector
                        originalSettings={originalSettings}
                        groupFilterModules={data?.modules}
                        groupFilters={groupFilters}
                        employeeFilters={employeeFilters}
                        strictRecipientsFiltering={strictRecipientsFiltering}
                        isFormLive={isFormLive}
                        isFormArchived={isFormArchived}
                        onChangeGroupFilters={handleChangeGroupFilters}
                        onSetStrictRecipientsFiltering={onSetStrictRecipientsFiltering}
                        onSettingsChange={onSettingsChange}
                        onEmployeeFiltersChange={handleEmployeeFiltersChange}
                    />
                )}

            {assignmentType === FORM_ASSIGNMENT_TYPE.students &&
                (audience === FORM_AUDIENCE_TYPE.students ||
                    audience === FORM_AUDIENCE_TYPE.parentsForChildCompletion ||
                    audience === FORM_AUDIENCE_TYPE.previousSchool ||
                    audience === FORM_AUDIENCE_TYPE.externalUsers) && (
                    <StudentFiltersSelector
                        originalSettings={originalSettings}
                        groupFilterModules={data?.modules
                            .filter(m => m.membersType !== GROUP_MEMBERS_TYPE.employee)
                            .map(({
                                      filters,
                                      ...rest
                                  }) => ({
                                filters: filters.filter(filter => filter.membersType !== GROUP_MEMBERS_TYPE.employee),
                                ...rest
                            })) || []}
                        groupFilters={groupFilters}
                        studentFilters={studentFilters}
                        applicationFilters={applicationFilters}
                        enquiryFilters={enquiryFilters}
                        strictRecipientsFiltering={strictRecipientsFiltering}
                        isFormLive={isFormLive}
                        isFormArchived={isFormArchived}
                        onChangeGroupFilters={handleChangeGroupFilters}
                        onSetStrictRecipientsFiltering={onSetStrictRecipientsFiltering}
                        onStudentFiltersChange={handleStudentFiltersChange}
                        schoolInfo={schoolInfo}
                    />
                )}
        </Box>
    );
};

function StudentFiltersSelector(
    {
        originalSettings,
        groupFilterModules,
        groupFilters,
        studentFilters,
        applicationFilters,
        enquiryFilters,
        strictRecipientsFiltering,
        isFormLive,
        isFormArchived,
        onChangeGroupFilters,
        onSetStrictRecipientsFiltering,
        onStudentFiltersChange,
        schoolInfo
    }) {
    return (
        <SettingsSectionBase
            header="Recipients"
            headerIcon={<PeopleAltOutlinedIcon fontSize="small"/>}
            subHeader="Select student groups and individual students to assign this form to."
        >
            <GroupFiltersTable
                groupFilters={groupFilters}
                groupFilterModules={groupFilterModules}
                originalSettings={originalSettings}
                strictRecipientsFiltering={strictRecipientsFiltering}
                onChangeGroupFilters={onChangeGroupFilters}
                onChangeStrictRecipientsFiltering={onSetStrictRecipientsFiltering}
                isFormLive={isFormLive}
                isFormArchived={isFormArchived}
                header="Add students or applicants using filters and groups"
            />
            <SettingsSection>
                <Typography variant="subtitle1" fontWeight="bold">Add individual student or applicant to form</Typography>
                <PaginatedAutocomplete
                    isSelectionDisabled={isFormArchived}
                    textFieldProps={{
                        label: 'Applications',
                        placeholder: 'Enter name, surname, or full name',
                        required: true,

                    }}
                    fetchFn={useLazyGetStudentFiltersQuery}
                    fetchFnProps={{
                        queryParams: {
                            pupilType: 'applicant'
                        }
                    }}
                    getOptionLabel={(option) => option.label}
                    value={applicationFilters.filter(filter => !filter.isDeleted)}
                    onChange={(e, newValue) => onStudentFiltersChange({applicationFilters: mergeArraysByKey(newValue, originalSettings.applicationFilters)})}
                />
                <PaginatedAutocomplete
                    isSelectionDisabled={isFormArchived}
                    textFieldProps={{
                        label: 'Students',
                        placeholder: 'Enter name, surname, or full name',
                        required: true,
                        disabled: isFormArchived,
                    }}
                    fetchFn={useLazyGetStudentFiltersQuery}
                    fetchFnProps={{
                        queryParams: {
                            pupilType: 'student'
                        }
                    }}
                    getOptionLabel={(option) => option.label}
                    value={studentFilters.filter(filter => !filter.isDeleted)}
                    onChange={(e, newValue) => onStudentFiltersChange({studentFilters: mergeArraysByKey(newValue, originalSettings.studentFilters)})}
                />
                {
                    schoolInfo == null || schoolInfo.disableEnquiriesSync
                        ? null
                        : <PaginatedAutocomplete
                            isSelectionDisabled={isFormArchived}
                            textFieldProps={{
                                label: 'Enquiries',
                                placeholder: 'Enter name, surname, or full name',
                                required: true,
                                disabled: isFormArchived,
                            }}
                            fetchFn={useLazyGetStudentFiltersQuery}
                            fetchFnProps={{
                                queryParams: {
                                    pupilType: 'enquiry'
                                }
                            }}
                            getOptionLabel={(option) => option.label}
                            value={enquiryFilters.filter(filter => !filter.isDeleted)}
                            onChange={(e, newValue) => onStudentFiltersChange({enquiryFilters: mergeArraysByKey(newValue, originalSettings.enquiryFilters)})}
                        />
                }
            </SettingsSection>
        </SettingsSectionBase>
    );
}

function EmployeeFiltersSelector(
    {
        originalSettings,
        groupFilterModules,
        groupFilters,
        employeeFilters,
        strictRecipientsFiltering,
        isFormLive,
        isFormArchived,
        onChangeGroupFilters,
        onSetStrictRecipientsFiltering,
        onEmployeeFiltersChange
    }) {
    const filteredGroupFilterModules = useMemo(() => groupFilterModules
            ?.filter((group) =>
                group.name === 'EMPLOYEES' ||
                group.name?.toLowerCase().includes('school group') ||
                group.name?.toLowerCase().includes('my group'))
            .map(({filters, ...rest}) => ({
                filters: filters.filter(filter => filter.membersType === GROUP_MEMBERS_TYPE.employee),
                ...rest
            })) || [],
        [groupFilterModules?.length]);

    return (
        <SettingsSectionBase
            header="Recipients"
            headerIcon={<AssignmentIndOutlinedIcon fontSize="small"/>}
            subHeader="Select employee groups and individual employees to assign this form to."
        >
            <GroupFiltersTable
                groupFilters={groupFilters}
                groupFilterModules={filteredGroupFilterModules}
                originalSettings={originalSettings}
                strictRecipientsFiltering={strictRecipientsFiltering}
                onChangeStrictRecipientsFiltering={onSetStrictRecipientsFiltering}
                onChangeGroupFilters={onChangeGroupFilters}
                header="Add employees using filters and groups"
                isFormLive={isFormLive}
                isFormArchived={isFormArchived}
            />
            <SettingsSection>
                <Typography variant="subtitle1" fontWeight="bold">Add individual employee to form</Typography>
                <PaginatedAutocomplete
                    isSelectionDisabled={isFormArchived}
                    textFieldProps={{
                        label: 'Employees',
                        placeholder: 'Enter name, surname, or full name',
                        required: true,
                    }}
                    fetchFn={useLazyGetAllEmployeeProfilesQuery}
                    getOptionLabel={(option) => option.label || option.preferredForename + ' ' + option.preferredSurname}
                    value={employeeFilters.filter(filter => !filter.isDeleted)}
                    onChange={(e, newValue) => {
                        onEmployeeFiltersChange(mergeArraysByKey(newValue, originalSettings.employeeFilters));
                    }}
                />
            </SettingsSection>
        </SettingsSectionBase>
    );
}

function GroupFiltersTable(
    {
        originalSettings,
        groupFilters,
        groupFilterModules,
        onChangeGroupFilters,
        onChangeStrictRecipientsFiltering,
        isFormLive,
        isFormArchived,
        strictRecipientsFiltering,
        header
    }) {
    const [openAddGroupDialog, setOpenAddGroupDialog] = useState(false);
    const data = groupFilters.filter(filter => !filter.isDeleted);

    return (
        <>
            <SettingsSection>
                <Stack
                    justifyContent="space-between"
                    flexDirection="row"
                    alignItems="center"
                    gap={2}
                    sx={{width: '100%'}}
                >
                    <Typography variant="subtitle1" fontWeight="bold">
                        {header}
                    </Typography>
                    <Button
                        sx={{minWidth: 160}}
                        variant="outlined"
                        startIcon={<AddOutlinedIcon/>}
                        disabled={isFormArchived}
                        onClick={() => {
                            if (isFormArchived) return;
                            setOpenAddGroupDialog(true);
                        }}
                    >
                        Add Group
                    </Button>
                </Stack>
            </SettingsSection>

            {data.some(filter => filter.deleted) && (
                <SettingsSection>
                    <Box
                        display="flex"
                        alignItems="flex-start"
                        gap={1.5}
                        sx={{
                            p: 2.5,
                            backgroundColor: 'rgba(255, 152, 0, 0.08)',
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'rgba(255, 152, 0, 0.3)',
                            mb: 1
                        }}
                    >
                        <WarningIcon
                            sx={{
                                color: 'warning.main',
                                fontSize: 20,
                                mt: 0.25,
                                flexShrink: 0
                            }}
                        />
                        <Typography
                            variant="body2"
                            sx={{
                                color: 'text.primary',
                                lineHeight: 1.5,
                                fontSize: '0.875rem'
                            }}
                        >
                            Some of the groups used in the filter configuration are deactivated. Please review the form
                            filters to ensure the setup is still valid and remove deactivated groups from the filters.
                            New form requests will not be sent for this form until the deactivated groups are removed
                            from the filters.
                        </Typography>
                    </Box>
                </SettingsSection>
            )}

            <SettingsSection>
                <AdmicityTableV2
                    emptyStateMinHeight={80}
                    noRowsOverlay={{ text: 'No groups' }}
                    columns={[
                            {
                                accessorKey: 'displayName',
                                header: 'Group Name',
                                grow: true,
                                Cell: ({
                                           cell,
                                           row
                                       }) => {
                                    const isDeleted = row.original.deleted;
                                    const content = (
                                        <Typography
                                            sx={{
                                                color: isDeleted ? 'error.main' : 'inherit',
                                                textDecoration: isDeleted ? 'line-through' : 'none',
                                                opacity: isDeleted ? 0.7 : 1
                                            }}
                                        >
                                            {cell.getValue()}
                                        </Typography>
                                    );

                                    return isDeleted
                                        ? (
                                            <Tooltip
                                                title="This group has been deactivated. Please remove it from the filters to ensure the setup is still valid. New form requests will not be sent for this form until the deactivated groups are removed from the filters."
                                                arrow
                                            >
                                                <Box sx={{cursor: 'help'}}>
                                                    {content}
                                                </Box>
                                            </Tooltip>
                                        )
                                        : content;
                                }
                            },
                            {
                                accessorKey: 'excluded',
                                header: 'Status',
                                size: 100,
                                grow: false,
                                Cell: ({cell}) => (
                                    <AdmicityStatus {...getGroupFilterStatusInfo(cell.getValue())}/>
                                )
                            }
                        ]}
                        data={groupFilters.filter(filter => !filter.isDeleted)}
                        tableProps={{
                            layoutMode: 'grid',
                            enableRowActions: !isFormArchived,
                            enableSorting: false,
                            enableFilters: false,
                            enableHiding: false,
                            enableFullScreenToggle: false,
                            enableColumnResizing: false,
                            enableBottomToolbar: false,
                            enableTopToolbar: false,
                            displayColumnDefOptions: {
                                'mrt-row-actions': {
                                    size: 50,
                                    grow: false,
                                },
                            },
                            muiTableBodyProps: {
                                sx: {
                                    '& :last-child td': {
                                        borderBottom: 0,
                                    },
                                }
                            },
                            muiTablePaperProps: {
                                variant: 'outlined',
                                elevation: 0,
                                sx: {
                                    flexDirection: 'column',
                                    flexGrow: 1
                                }
                            },
                            muiTableContainerProps: {
                                sx: {
                                    maxHeight: 350,
                                    minHeight: 'auto',
                                    border: 'none'
                                }
                            },
                            renderRowActions: !isFormArchived
                                ? ({row}) => (
                                    <IconButton
                                        onClick={() => {
                                            const filters = groupFilters.filter(filter => row.original.id !== filter.id);
                                            const newFilters = mergeArraysByKeys(
                                                filters,
                                                originalSettings.groupFilters,
                                                ['id', 'excluded']
                                            );

                                            onChangeGroupFilters(newFilters);
                                        }}
                                    >
                                        <DeleteOutlineRoundedIcon fontSize="small"/>
                                    </IconButton>
                                )
                                : undefined
                        }}
                    />
                </SettingsSection>

            {data.length > 0 && (
                <FormControlLabel
                    sx={{gap: 1}}
                    control={
                        <Checkbox
                            sx={{
                                padding: 0,
                                marginLeft: 1
                            }}
                            checked={strictRecipientsFiltering}
                            onChange={async (e) => {
                                if (isFormArchived) return;
                                await onChangeStrictRecipientsFiltering(e.target.checked);
                            }}
                            disabled={isFormLive || isFormArchived}
                        />
                    }
                    label={
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5
                        }}>
                            Must appear in all these groups
                            <Tooltip
                                title="When ticked, recipients must belong to every 'Included' group listed above to receive the form. When unticked, they will receive it if they appear in any of the 'Included' groups."
                                placement="right"
                            >
                                <InfoOutlinedIcon
                                    fontSize="small"
                                    sx={{
                                        color: 'text.secondary',
                                        cursor: 'pointer'
                                    }}
                                />
                            </Tooltip>
                        </Box>
                    }
                />
            )}

            <AddStudentGroupDialog
                open={openAddGroupDialog && !isFormArchived}
                groupFilters={groupFilterModules}
                onClose={() => setOpenAddGroupDialog(false)}
                onChange={(filter => {
                    if (isFormArchived) return;

                    const newFilter = {
                        id: filter.filterId,
                        displayName: filter.paths.map(x => x.name).join(' - '),
                        excluded: filter.excluded
                    };

                    const filters = [...groupFilters];
                    const filterIndex = filters.findIndex(x => x.id === newFilter.id);

                    if (filterIndex >= 0) {
                        filters[filterIndex] = newFilter;
                    } else {
                        filters.push(newFilter);
                    }

                    const newFilters = mergeArraysByKeys(filters, originalSettings.groupFilters, ['id', 'excluded']);

                    onChangeGroupFilters(newFilters);
                })}
            />
        </>
    );
}

export default RegularFormSettings;
